import React, { useCallback } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Code,
} from "@chakra-ui/react";
import { QRCodeSVG } from "qrcode.react";
import { FiPrinter, FiDownload } from "react-icons/fi";

/**
 * QR code display component with print and download options.
 * @param {{ url: string, size?: number, label?: string }} props
 */
export default function QrCodeDisplay({ url, size = 200, label }) {
  const handlePrint = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head><title>QR Code</title>
      <style>
        body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
        .label { font-size: 14px; color: #666; margin-top: 16px; }
        .url { font-size: 10px; color: #999; margin-top: 8px; word-break: break-all; max-width: 300px; text-align: center; }
      </style>
      </head>
      <body>
        <div id="qr"></div>
        ${label ? `<div class="label">${label}</div>` : ""}
        <div class="url">${url}</div>
        <script>window.onload = () => window.print();</script>
      </body>
      </html>
    `);

    // Render QR into the print window
    const svg = document.querySelector("#qr-display svg");
    if (svg) {
      printWindow.document.getElementById("qr").innerHTML = svg.outerHTML;
    }
    printWindow.document.close();
  }, [url, label]);

  const handleDownload = useCallback(() => {
    const svg = document.querySelector("#qr-display svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    canvas.width = size * 2;
    canvas.height = size * 2;
    const ctx = canvas.getContext("2d");

    const img = new window.Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(svgUrl);

      const link = document.createElement("a");
      link.download = "qr-code.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = svgUrl;
  }, [size]);

  if (!url) return null;

  return (
    <VStack spacing={3} align="center">
      <Box
        id="qr-display"
        bg="white"
        p={4}
        borderRadius="lg"
        display="inline-block"
      >
        <QRCodeSVG value={url} size={size} level="H" />
      </Box>

      {label && (
        <Text fontSize="sm" color="gray.300" fontWeight="500" textAlign="center">
          {label}
        </Text>
      )}

      <Code
        fontSize="xs"
        color="gray.500"
        bg="gray.800"
        p={2}
        borderRadius="md"
        maxW="100%"
        wordBreak="break-all"
        textAlign="center"
      >
        {url}
      </Code>

      <HStack spacing={2}>
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          leftIcon={<FiPrinter />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          leftIcon={<FiDownload />}
          onClick={handleDownload}
        >
          Download PNG
        </Button>
      </HStack>
    </VStack>
  );
}
